import { Box, Button, Group, Modal, Paper, Stack, Text, Typography } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'

import { TranscriptView } from '@/components/TranscriptView'
import { theme } from '@/configs/theme'
import * as commonClasses from '@/styles/componentCommons.css'
import * as styles from '@/styles/ContentModuleScreen.css'
import { transcriptHasSpeakerLabels } from '@/utils/transcript'
// No HTML troubleshooting; backend provides markdown

const VISIBILITY_TRANSITION_DURATION = 300

type ModalContent = {
  data?: string
  title?: string
  subtitle?: string
  renderAsMarkdown?: boolean
}

type OpenModalArgs = ModalContent & {
  opts?: {
    markdown?: boolean
    styles?: {
      content?: React.CSSProperties
      // Add other style overrides as needed
    }
  }
}

export type ContentDataModal = {
  openModal: (content: OpenModalArgs) => void
  closeModal: () => void
}

export const ContentDataModal = forwardRef<ContentDataModal, any>(function ContentDataModal(_props, ref) {
  const closeTimeout = useRef<NodeJS.Timeout>(undefined)

  const [isOpen, { open, close }] = useDisclosure()
  const [modalContent, setModalContent] = useState<ModalContent | null>(null)
  const [modalStyles, setModalStyles] = useState<NonNullable<OpenModalArgs['opts']>['styles']>(undefined)

  function openModal(content: OpenModalArgs) {
    const { opts, data, ...restContent } = content

    clearTimeout(closeTimeout.current)
    closeTimeout.current = undefined

    setModalStyles(opts?.styles)

    // Backend provides markdown content; render via ReactMarkdown when flagged
    const shouldRenderAsMarkdown = !!data && !!opts?.markdown
    setModalContent({ ...restContent, data, renderAsMarkdown: shouldRenderAsMarkdown })

    open()
  }

  function closeModal() {
    close()

    closeTimeout.current = setTimeout(() => setModalContent(null), VISIBILITY_TRANSITION_DURATION)
  }

  useImperativeHandle(ref, () => ({ openModal, closeModal }))

  return (
    <Modal
      opened={isOpen}
      centered
      trapFocus
      radius="md"
      padding="lg"
      closeOnEscape
      closeOnClickOutside
      withCloseButton={false}
      size={theme.rem(1200)}
      onClose={closeModal}
      styles={{
        content: {
          ...modalStyles?.content,
        },
      }}
      transitionProps={{ duration: VISIBILITY_TRANSITION_DURATION }}
      title={
        modalContent?.title && (
          <Stack gap={0}>
            <Text size="xl" fw={600}>
              {modalContent?.title}
            </Text>

            {!!modalContent?.subtitle && (
              <Text size="md" c="dimmed">
                {modalContent.subtitle}
              </Text>
            )}
          </Stack>
        )
      }
      classNames={{
        body: commonClasses.stickyModalFooterBody,
      }}>
      <Box className={commonClasses.stickyModalFooterContentWrapper}>
        <Paper bg={theme.lighten(theme.colors.primaryColors[1], 0.7)} radius="sm" p="md" pb="xl" shadow="0">
          <Typography className={styles.typographyProvider}>
            {modalContent?.data && transcriptHasSpeakerLabels(modalContent.data) ? (
              <TranscriptView content={modalContent.data} />
            ) : modalContent?.data && modalContent?.renderAsMarkdown ? (
              <div className="markdown-content">
                <ReactMarkdown>{modalContent.data}</ReactMarkdown>
              </div>
            ) : (
              <Text style={{ whiteSpace: 'pre-wrap' }}>{modalContent?.data ?? ''}</Text>
            )}
          </Typography>
        </Paper>
      </Box>

      <Modal.Header className={commonClasses.stickyModalFooter}>
        <Group gap="xl">
          <Button size="sm" variant="light" color="gray" onClick={closeModal}>
            Close
          </Button>
        </Group>
      </Modal.Header>
    </Modal>
  )
})
